import { ReactNode, useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, inputClass, money, toCents } from '../../../lib/programs';
import { EventMoney, TicketType, dateText } from '../../../lib/eventMoney';
import type { SimplePanelProps } from '../adapt';
import { headRow, num, td, th } from '../logistics';
import { usePresent } from '../dayOfTypes';
import {
  CountsOnly,
  MoneyField,
  budgetLeadKey,
  dollarsIn,
  moneyError,
  useEventMoney,
  useMoneyRefresh,
  useOpenTreasuryRequest,
} from './BudgetPanel';

function RequestDialog({ eventId, data, onDone }: { eventId: string; data: EventMoney; onDone: () => void }) {
  const r = data.finance!.eventbriteRequest;
  const [form, setForm] = useState({
    salesOpen: r.salesOpen ?? '',
    salesClose: r.salesClose ?? '',
    expected: r.expected == null ? '' : String(r.expected),
    feesAbsorbed: r.feesAbsorbed,
    costCentre: data.finance!.costCentre ?? '',
  });
  const [tickets, setTickets] = useState(
    r.ticketTypes.map((t) => ({
      name: t.name,
      price: dollarsIn(t.priceCents),
      capacity: t.capacity == null ? '' : String(t.capacity),
    })),
  );
  const refresh = useMoneyRefresh(eventId);
  const save = useMutation(
    async () => {
      const ticketTypes: TicketType[] = tickets.map((t) => {
        const price = toCents(t.price);
        if (Number.isNaN(price)) throw new Error(`${t.name}: enter a price like 75.00`);
        return { name: t.name, priceCents: price, capacity: t.capacity === '' ? null : Number(t.capacity) };
      });
      return api.put(`/events/${eventId}/money/settings`, {
        costCentre: form.costCentre,
        eventbriteRequest: {
          salesOpen: form.salesOpen || null,
          salesClose: form.salesClose || null,
          expected: form.expected === '' ? null : Number(form.expected),
          feesAbsorbed: form.feesAbsorbed,
          ticketTypes,
        },
      });
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const setTicket = (i: number, k: 'name' | 'price' | 'capacity', v: string) =>
    setTickets(tickets.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <Modal
      title="Details for York Treasury"
      why="What the Eventbrite request says. The tickets are the same ones as on Registration & outreach."
      onClose={onDone}
      width={720}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Save the details
          </Button>
          <Button onClick={onDone}>Cancel</Button>
        </>
      }
    >
      {save.error ? moneyError(save.error) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoneyField label="Sales open">
          <input
            type="date"
            className={inputClass}
            value={form.salesOpen}
            onChange={(e) => setForm({ ...form, salesOpen: e.target.value })}
          />
        </MoneyField>
        <MoneyField label="Sales close">
          <input
            type="date"
            className={inputClass}
            value={form.salesClose}
            onChange={(e) => setForm({ ...form, salesClose: e.target.value })}
          />
        </MoneyField>
        <MoneyField label="Expected buyers">
          <input
            className={inputClass}
            value={form.expected}
            onChange={(e) => setForm({ ...form, expected: e.target.value })}
            inputMode="numeric"
          />
        </MoneyField>
        <MoneyField label="Cost centre">
          <input
            className={inputClass}
            value={form.costCentre}
            onChange={(e) => setForm({ ...form, costCentre: e.target.value })}
          />
        </MoneyField>
        <label className="md:col-span-2 inline-flex items-center gap-3 min-h-[44px] text-ink">
          <input
            type="checkbox"
            className="w-[18px] h-[18px] accent-accent"
            checked={form.feesAbsorbed}
            onChange={(e) => setForm({ ...form, feesAbsorbed: e.target.checked })}
          />
          DIVERT absorbs the fees
        </label>
      </div>
      <table className="w-full mt-4">
        <thead>
          <tr className={headRow}>
            <th className={th}>Ticket</th>
            <th className={`${th} ${num} w-[28%]`}>Price</th>
            <th className={`${th} ${num} w-[24%]`}>Capacity</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t, i) => (
            <tr key={i} className="border-b border-line">
              <td className={`${td} py-[6px]`}>
                <input
                  className={inputClass}
                  value={t.name}
                  onChange={(e) => setTicket(i, 'name', e.target.value)}
                  aria-label="Ticket name"
                />
              </td>
              <td className={`${td} py-[6px]`}>
                <input
                  className={`${inputClass} text-right`}
                  value={t.price}
                  placeholder="$"
                  onChange={(e) => setTicket(i, 'price', e.target.value)}
                  aria-label={`Price of ${t.name || 'this ticket'}`}
                  inputMode="decimal"
                />
              </td>
              <td className={`${td} py-[6px]`}>
                <input
                  className={`${inputClass} text-right`}
                  value={t.capacity}
                  placeholder="No limit"
                  onChange={(e) => setTicket(i, 'capacity', e.target.value)}
                  aria-label={`Capacity of ${t.name || 'this ticket'}`}
                  inputMode="numeric"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button
        variant="quiet"
        size="sm"
        className="px-0 mt-2"
        onClick={() => setTickets([...tickets, { name: 'Ticket', price: '', capacity: '' }])}
      >
        Add a ticket type
      </Button>
    </Modal>
  );
}

function StepRow({ n, done, children, action }: { n: number; done: boolean; children: ReactNode; action?: ReactNode }) {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-[10px] border-b border-line text-[15px] leading-[22px] text-ink">
      <span
        aria-hidden
        className={`flex-none w-6 h-6 rounded-full border text-center text-small font-semibold leading-[22px] ${
          done ? 'bg-accent border-accent text-surface' : 'border-line-strong text-ink-2'
        }`}
      >
        {done ? '✓' : n}
      </span>
      <span className="flex-[1_1_300px]">
        <span className="sr-only">{done ? 'Done: ' : `Step ${n}: `}</span>
        {children}
      </span>
      {action}
    </li>
  );
}

/** Eventbrite through York Treasury: the request before, the sales report after. */
export default function TreasuryPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useEventMoney(eventId);
  const leadHere = usePresent(budgetLeadKey(eventId));
  const refresh = useMoneyRefresh(eventId);
  const [editing, setEditing] = useState(false);
  const mark = useMutation(
    async (body: { which: string; undo?: boolean }) => api.post(`/events/${eventId}/money/treasury`, body),
    { onSuccess: refresh },
  );
  const open = useOpenTreasuryRequest(eventId);
  if (q.isLoading) return <SkeletonRows rows={3} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load.')}</ErrorNotice>;
  const d = q.data;
  if (!d.visible)
    return leadHere ? null : (
      <Panel>
        <PanelHeader title="Eventbrite and York Treasury" />
        <PanelBody>
          <CountsOnly data={d} />
        </PanelBody>
      </Panel>
    );
  const t = d.finance!.treasury;
  const r = d.finance!.eventbriteRequest;
  const names = r.ticketTypes.map((x) => x.name);
  const ticketText = names.length
    ? `${names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]} ticket${
        names.length === 1 ? '' : 's'
      }`
    : 'no ticket types';
  const priced = r.ticketTypes.filter((x) => x.priceCents != null);
  const undo = (which: string) =>
    canEdit ? (
      <Button size="sm" variant="quiet" onClick={() => mark.mutate({ which, undo: true })}>
        Undo
      </Button>
    ) : null;
  return (
    <Panel>
      <PanelHeader
        title="Eventbrite and York Treasury"
        note={t.requestSentAt ? `request sent ${dateText(t.requestSentAt)}` : undefined}
      />
      <PanelBody>
        <p className="text-[15px] leading-[22px] text-ink mb-3">
          Sales {r.salesOpen ? dateText(r.salesOpen) : '–'} to {r.salesClose ? dateText(r.salesClose) : '–'} ·{' '}
          {ticketText}
          {priced.length ? ` (${priced.map((x) => `${x.name} ${money(x.priceCents)}`).join(', ')})` : ''} ·{' '}
          {r.expected != null ? `${r.expected} expected buyers` : 'expected buyers not set'}
          {r.feesAbsorbed ? ' · DIVERT absorbs the fees' : ''}
        </p>
        <ol className="max-w-[900px]">
          <StepRow
            n={1}
            done={false}
            action={
              canEdit ? (
                <Button size="sm" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              ) : null
            }
          >
            Check the details.
          </StepRow>
          <StepRow
            n={2}
            done={false}
            action={
              <Button size="sm" disabled={open.isLoading} onClick={() => open.mutate()}>
                Open the request
              </Button>
            }
          >
            Open the request, then print it or copy it into Treasury’s questionnaire.
          </StepRow>
          <StepRow
            n={3}
            done={Boolean(t.requestSentAt)}
            action={
              t.requestSentAt ? (
                undo('request')
              ) : canEdit ? (
                <Button size="sm" disabled={mark.isLoading} onClick={() => mark.mutate({ which: 'request' })}>
                  Mark the request sent
                </Button>
              ) : null
            }
          >
            {t.requestSentAt ? (
              <>Sent to Treasury {dateText(t.requestSentAt)}.</>
            ) : (
              <>
                Mark the request sent.{' '}
                <span className="text-small text-ink-3">Treasury needs it at least six weeks before sales open.</span>
              </>
            )}
          </StepRow>
          <StepRow
            n={4}
            done={Boolean(t.salesReportSentAt)}
            action={
              t.salesReportSentAt ? (
                undo('sales_report')
              ) : canEdit ? (
                <Button size="sm" disabled={mark.isLoading} onClick={() => mark.mutate({ which: 'sales_report' })}>
                  Mark the report sent
                </Button>
              ) : null
            }
          >
            {t.salesReportSentAt ? (
              <>
                After the event: sales report sent to Treasury and General Accounting {dateText(t.salesReportSentAt)}.
              </>
            ) : (
              <>After the event: send the sales report to Treasury and General Accounting.</>
            )}
          </StepRow>
        </ol>
        {mark.error || open.error ? (
          <div className="mt-3">
            <ErrorNotice>{errorMessage(mark.error || open.error, 'Could not do that.')}</ErrorNotice>
          </div>
        ) : null}
      </PanelBody>
      {editing ? <RequestDialog eventId={eventId} data={d} onDone={() => setEditing(false)} /> : null}
    </Panel>
  );
}
